// ╔══════════════════════════════════════════════════════════════╗
// ║  Audit log viewer (admin only)                                 ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtAR, fmtRelativeTime } from '../lib/format.js';
import { Badge, Btn, Card } from '../components/ui.jsx';

const ACTION_TONES = {
  create: 'success',
  update: 'info',
  delete: 'danger',
  approve: 'success',
  reject: 'warn',
  login: 'neutral',
  logout: 'neutral',
  export: 'info',
  import: 'info',
};

const ACTION_LABEL = {
  create: 'إنشاء',
  update: 'تعديل',
  delete: 'حذف',
  approve: 'اعتماد',
  reject: 'رفض',
  login: 'دخول',
  logout: 'خروج',
  export: 'تصدير',
  import: 'استيراد',
};

export default function AuditPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['audit', { page }],
    queryFn: () =>
      api.get('/audit', { params: { page, pageSize: 30 } }).then((r) => r.data),
  });

  const entries = data?.entries ?? [];
  const pagination = data?.pagination;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
          الأمان
        </div>
        <h1 className="text-2xl md:text-3xl font-black mt-1">سجل التدقيق</h1>
        <p className="text-sm text-ink-400 mt-1">
          كل عملية تعديل في النظام مسجّلة. للقراءة فقط — لا يمكن حذف هذه السجلات.
        </p>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {['الوقت', 'المستخدم', 'العملية', 'الكيان', 'العنصر', 'الـ IP'].map(
                  (h, i) => (
                    <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-ink-400">
                    جاري التحميل…
                  </td>
                </tr>
              )}
              {!isLoading && entries.length === 0 && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-ink-400">
                    لا توجد سجلات
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {fmtRelativeTime(e.createdAt)}
                  </td>
                  <td className="px-4 py-3 font-semibold">{e.userName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge tone={ACTION_TONES[e.action] ?? 'neutral'}>
                      {ACTION_LABEL[e.action] ?? e.action}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.entity}</td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-400">
                    {e.entityId ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-400">{e.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 text-xs text-ink-400 border-t border-ink-100 dark:border-ink-800">
            <span>
              {fmtAR(pagination.page)} من {fmtAR(pagination.totalPages)} ({fmtAR(pagination.total)} سجل)
            </span>
            <div className="flex gap-1">
              <Btn variant="ghost" size="sm" icon="chevR" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1}>السابق</Btn>
              <Btn variant="ghost" size="sm" iconEnd="chevL" onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={pagination.page >= pagination.totalPages}>التالي</Btn>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
